import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDeviceHistory } from '../api/queries'
import type {
  DeviceHistory,
  DeviceHistoryDeviation,
  DeviceHistoryPhoto,
  DeviceMeasurementSeries
} from '../api/types'
import { useAuthedBlob } from '../lib/photos'
import { describeExpiry, formatDate } from '../lib/format'
import { Card } from './ui'

/**
 * Enhedens historik samlet ét sted (docs/DECISIONS.md beslutning 062).
 *
 * Det er dét, montøren ser, når han lige har scannet mærkatet og står foran
 * anlægget: hvem var her sidst, hvad var galt dengang, hvad måler den nu i
 * forhold til før, hvad står der åbent på sagen, og er der garanti på den.
 *
 * <b>Ingen af oplysningerne er nye.</b> De ligger allerede under service, filer,
 * mangler og garantier - men på hver sin side, og i et teknikrum uden dækning
 * bliver fire opslag til nul. Kortet stiller dem sammen.
 */
export function DeviceHistoryCard({ deviceId }: { deviceId: number }) {
  const { data } = useDeviceHistory(deviceId)

  // Ingen fejlboks: historikken er en hjælp, ikke det siden handler om. Kan den
  // ikke hentes, skal enhedens egne oplysninger og "Start service" stadig virke.
  if (!data) return null

  return (
    <>
      <LastVisitCard history={data} />
      <DeviationsCard deviations={data.deviations} />
      <MeasurementsCard series={data.measurements} />
      <PhotosCard photos={data.photos} />
      <WarrantiesCard warranties={data.warranties} />
      <DefectsCard defects={data.defects} />
    </>
  )
}

/** Hvem var her sidst, og hvad blev der skrevet. Det spørgsmål stilles hver gang. */
function LastVisitCard({ history }: { history: DeviceHistory }) {
  const visit = history.lastVisit

  if (!visit) {
    return (
      <Card title="Sidste eftersyn">
        <div className="muted small">
          Enheden er aldrig serviceret færdig i systemet. Der er derfor hverken målinger eller
          afvigelser at sammenligne med.
        </div>
      </Card>
    )
  }

  return (
    <Card
      title="Sidste eftersyn"
      actions={
        <Link className="btn btn--sm" to={`/service-visits/${visit.id}`}>
          Se rapporten
        </Link>
      }
    >
      <div className="row" style={{ gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>{formatDate(visit.completedAt ?? visit.startedAt)}</div>
        {visit.performedBy && <div className="small">{visit.performedBy.displayName}</div>}
        {visit.deviationCount > 0 ? (
          <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>
            {visit.deviationCount} afvigelse{visit.deviationCount === 1 ? '' : 'r'}
          </span>
        ) : (
          <span className="pill" style={{ background: '#DCFCE7', color: '#166534' }}>
            Uden afvigelser
          </span>
        )}
      </div>

      <div className="tiny muted" style={{ marginTop: 4 }}>
        {visit.checklistName ?? 'Uden checkliste'}
        {history.visitCount > 1 && ` · ${history.visitCount} eftersyn i alt`}
        {visit.nextServiceDate && ` · næste ${formatDate(visit.nextServiceDate)}`}
      </div>

      {visit.instrumentName && (
        <div
          className="tiny"
          style={{ marginTop: 6, color: visit.instrumentWasValid === false ? '#B45309' : undefined }}
        >
          Målt med {visit.instrumentName}
          {/* En maaling fra et ukalibreret instrument er et gaet (beslutning
              047). Staar det ikke ved tallene, ser de sikrere ud, end de er. */}
          {visit.instrumentWasValid === false && ' - kalibreringen var udløbet den dag'}
        </div>
      )}

      {visit.notes && (
        <div className="small" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          {visit.notes}
        </div>
      )}
    </Card>
  )
}

/**
 * Afvigelserne fra sidste eftersyn.
 *
 * Overskriften siger "ved sidste eftersyn" og ikke "åbne": en afvigelse kan ikke
 * lukkes i systemet - den er en iagttagelse fra én dag, og næste eftersyn
 * skriver en ny (beslutning 062).
 */
function DeviationsCard({ deviations }: { deviations: DeviceHistoryDeviation[] }) {
  if (deviations.length === 0) return null

  return (
    <Card title={`Afvigelser ved sidste eftersyn (${deviations.length})`}>
      <div className="tiny muted" style={{ marginTop: -4, marginBottom: 10 }}>
        Set {formatDate(deviations[0].occurredAt)}. Er de udbedret siden, står det i den nye
        rapport - ikke her.
      </div>

      <div className="stack" style={{ gap: 10 }}>
        {deviations.map((d, index) => (
          <div key={`${d.visitId}-${index}`} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <span
              style={{
                width: 6,
                alignSelf: 'stretch',
                minHeight: 32,
                borderRadius: 3,
                background: '#DC2626',
                flexShrink: 0
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div className="small" style={{ fontWeight: 600 }}>
                {d.text}
              </div>
              {d.value !== null && d.value !== undefined && (
                <div className="tiny" style={{ color: d.outOfRange ? '#B45309' : undefined }}>
                  Målt {formatNumber(d.value)}
                  {d.unit ? ` ${d.unit}` : ''}
                  {rangeText(d.minValue, d.maxValue, d.unit)}
                </div>
              )}
              {d.note && <div className="tiny muted">{d.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Måleværdierne over tid.
 *
 * <b>Det er her historikken tjener sig hjem.</b> Et enkelt tal siger ikke, om et
 * batteri er ved at være færdigt - det gør tre år i træk. Punkterne kobles på
 * tekst og enhed, så et omdøbt punkt starter en ny serie frem for at blive
 * sammenlignet med noget andet (beslutning 062).
 */
function MeasurementsCard({ series }: { series: DeviceMeasurementSeries[] }) {
  if (series.length === 0) return null

  return (
    <Card title="Målinger over tid">
      <div className="stack" style={{ gap: 14 }}>
        {series.map((s) => {
          const points = s.points
          const latest = points[points.length - 1]
          const previous = points.length > 1 ? points[points.length - 2] : null
          const change = previous ? latest.value - previous.value : null

          return (
            <div key={`${s.text}-${s.unit ?? ''}`}>
              <div className="row row--between" style={{ alignItems: 'baseline', gap: 8 }}>
                <div className="small" style={{ fontWeight: 600, minWidth: 0 }}>
                  {s.text}
                </div>
                <div
                  className="small"
                  style={{
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    color: latest.outOfRange ? '#B45309' : undefined
                  }}
                >
                  {formatNumber(latest.value)}
                  {s.unit ? ` ${s.unit}` : ''}
                </div>
              </div>

              <div className="tiny muted">
                {formatDate(latest.measuredAt)}
                {rangeText(s.minValue, s.maxValue, s.unit)}
                {change !== null && change !== 0 && (
                  <> · {change > 0 ? '+' : '−'}{formatNumber(Math.abs(change))} siden sidst</>
                )}
              </div>

              {points.length > 1 && (
                <div className="row" style={{ gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                  {points
                    .slice()
                    .reverse()
                    .map((p) => (
                      <div key={`${p.visitId}-${p.measuredAt}`} className="tiny">
                        <span style={{ color: p.outOfRange ? '#B45309' : undefined }}>
                          {formatNumber(p.value)}
                        </span>{' '}
                        <span className="muted">{yearOf(p.measuredAt)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

/**
 * Fotoene fra tidligere besøg.
 *
 * De hentes først, når man trykker: billederne ligger i SharePoint og skal
 * hentes ét ad gangen gennem API-et, og siden her åbnes i et teknikrum med
 * halvdårlig dækning (beslutning 023).
 */
function PhotosCard({ photos }: { photos: DeviceHistoryPhoto[] }) {
  const [show, setShow] = useState(false)

  if (photos.length === 0) return null

  return (
    <Card title={`Fotos fra tidligere besøg (${photos.length})`}>
      {show ? (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          {photos.map((photo) => (
            <HistoryPhoto key={`${photo.visitId}-${photo.photoId}`} photo={photo} />
          ))}
        </div>
      ) : (
        <button className="btn btn--sm" onClick={() => setShow(true)}>
          Hent billederne
        </button>
      )}
    </Card>
  )
}

function HistoryPhoto({ photo }: { photo: DeviceHistoryPhoto }) {
  const url = useAuthedBlob(`/api/service-visits/${photo.visitId}/photos/${photo.photoId}/content`)

  return (
    <Link to={`/service-visits/${photo.visitId}`} style={{ textAlign: 'center' }}>
      <div
        style={{
          width: 96,
          height: 96,
          borderRadius: 8,
          overflow: 'hidden',
          background: '#F1F5F9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {url ? (
          <img src={url} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span className="tiny muted">Henter ...</span>
        )}
      </div>
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {photo.kind === 'label' ? 'Mærkat' : 'Enhed'} · {yearOf(photo.uploadedAt)}
      </div>
    </Link>
  )
}

/** Garantierne på enheden - og hvem der hæfter, hvis den driller (beslutning 059). */
function WarrantiesCard({ warranties }: { warranties: DeviceHistory['warranties'] }) {
  if (!warranties.available) {
    // Modulet er slukket. Ingen grund til at fylde siden med den besked her -
    // huset har selv valgt det fra.
    return null
  }

  if (warranties.items.length === 0) return null

  return (
    <Card title="Garanti på enheden">
      <div className="stack" style={{ gap: 10 }}>
        {warranties.items.map((w) => (
          <div key={w.id} className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              style={{
                width: 6,
                alignSelf: 'stretch',
                minHeight: 36,
                borderRadius: 3,
                background: w.daysUntilExpiry <= 90 ? '#D97706' : w.color,
                flexShrink: 0
              }}
            />
            <div style={{ minWidth: 180, flex: 1 }}>
              <div className="small" style={{ fontWeight: 600 }}>
                {w.name}
              </div>
              {/* Hvem der haefter er det, kunden ringer om - derfor staar det,
                  og ikke bare typenavnet. */}
              <div className="tiny" style={{ color: w.color }}>
                {w.liable}
                {w.supplier && ` · ${w.supplier}`}
                {w.reference && ` · ${w.reference}`}
              </div>
            </div>
            <div
              className="small"
              style={{
                whiteSpace: 'nowrap',
                fontWeight: w.daysUntilExpiry <= 90 ? 600 : 400,
                color: w.daysUntilExpiry <= 90 ? '#B45309' : undefined
              }}
            >
              {describeExpiry(w.daysUntilExpiry)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Åbne mangler på den sag, enheden blev installeret under.
 *
 * Overskriften nævner sagen med vilje: manglen hører til sagen og ikke til
 * enheden (beslutning 058), og et punkt fra afleveringsforretningen er ikke det
 * samme som en fejl på anlægget.
 */
function DefectsCard({ defects }: { defects: DeviceHistory['defects'] }) {
  if (!defects.available) {
    // Her siges det derimod: brugeren kan ikke se sagen, og en tom liste ville
    // laeses som "ingen mangler" (beslutning 062).
    return defects.unavailableReason?.includes('adgang') ? (
      <Card title="Mangler på sagen">
        <div className="muted small">{defects.unavailableReason}</div>
      </Card>
    ) : null
  }

  if (defects.items.length === 0) return null

  return (
    <Card title={`Åbne mangler på ${defects.projectName ?? 'sagen'} (${defects.items.length})`}>
      <div className="stack" style={{ gap: 8 }}>
        {defects.items.map((d) => (
          <div key={d.id} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <span className="tiny muted" style={{ minWidth: 22 }}>
              {d.number}.
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="small">{d.title}</div>
              <div className="tiny muted">
                {[d.location, d.assignedUser?.displayName].filter(Boolean).join(' · ')}
                {d.isOverdue && (
                  <span style={{ color: '#B45309', fontWeight: 600 }}> · over fristen</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {defects.projectId && (
        <div style={{ marginTop: 10 }}>
          <Link className="small" to={`/projects/${defects.projectId}`}>
            Hele mangellisten
          </Link>
        </div>
      )}
    </Card>
  )
}

/** Intervallet i ord, når der er et. Uden det siger en måling ikke om den er god. */
function rangeText(min?: number | null, max?: number | null, unit?: string | null) {
  const suffix = unit ? ` ${unit}` : ''

  if (min !== null && min !== undefined && max !== null && max !== undefined) {
    return ` · forventet ${formatNumber(min)}-${formatNumber(max)}${suffix}`
  }
  if (min !== null && min !== undefined) return ` · mindst ${formatNumber(min)}${suffix}`
  if (max !== null && max !== undefined) return ` · højst ${formatNumber(max)}${suffix}`

  return ''
}

/** Dansk decimalkomma, og uden efterfølgende nuller: 12,60 bliver 12,6. */
function formatNumber(value: number) {
  return value.toLocaleString('da-DK', { maximumFractionDigits: 3 })
}

function yearOf(value: string) {
  return new Date(value).getFullYear()
}
